import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PublicFormService } from '../../../services/public-form.service';
import { CaptchaComponent } from '../../shared/captcha/captcha.component';

@Component({
  selector: 'app-plainte',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, TranslatePipe, RouterModule, CaptchaComponent],
  templateUrl: './plainte.html',
  styleUrl: './plainte.css',
})
export class PlainteComponent {
  @ViewChild('captcha') captcha?: CaptchaComponent;

  submitted = false;
  loading = false;
  reference = '';
  error = '';
  evidenceError = '';
  evidenceFiles: File[] = [];
  captchaAnswer = '';

  form = {
    name: '',
    phone: '',
    email: '',
    address: '',
    complaint_type: '',
    priority: 'medium',
    subject: '',
    description: '',
  };

  touched: Record<string, boolean> = {};

  complaintTypes = [
    { value: 'misleading_content',  label: 'plainte_type_trompeur' },
    { value: 'unethical_practices', label: 'plainte_type_ethique' },
    { value: 'copyright_violation', label: 'plainte_type_droit_auteur' },
    { value: 'fake_news',           label: 'plainte_type_fausses_infos' },
    { value: 'privacy_violation',   label: 'plainte_type_vie_privee' },
    { value: 'other',               label: 'commun_autre' },
  ];

  priorityLevels = [
    { value: 'low',    label: 'commun_priorite_faible',  css: 'priority-low' },
    { value: 'medium', label: 'commun_priorite_moyenne', css: 'priority-medium' },
    { value: 'high',   label: 'commun_priorite_elevee',  css: 'priority-high' },
    { value: 'urgent', label: 'commun_priorite_urgente', css: 'priority-urgent' },
  ];

  constructor(
    private publicFormService: PublicFormService,
    private translate: TranslateService,
  ) {}

  touch(field: string) {
    this.touched[field] = true;
  }

  isInvalid(field: string): boolean {
    if (!this.touched[field]) return false;
    const v = (this.form as any)[field];
    return !v || v.toString().trim() === '';
  }

  onFilesChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;
    this.evidenceError = '';
    const newFiles = Array.from(input.files);
    const combined = [...this.evidenceFiles, ...newFiles];
    if (combined.length > 5) {
      this.evidenceError = 'Maximum 5 fichiers autorisés.';
      return;
    }
    for (const f of newFiles) {
      if (f.size > 10 * 1024 * 1024) {
        this.evidenceError = `"${f.name}" dépasse 10 Mo.`;
        return;
      }
    }
    this.evidenceFiles = combined;
    input.value = '';
  }

  removeFile(i: number) {
    this.evidenceFiles = this.evidenceFiles.filter((_, idx) => idx !== i);
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' o';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
    return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
  }

  fileIsImage(f: File): boolean {
    return f.type.startsWith('image/');
  }

  onSubmit() {
    // Touch all required fields
    ['name','phone','email','address','complaint_type','subject','description'].forEach(f => this.touched[f] = true);
    if (['name','phone','email','address','complaint_type','subject','description'].some(f => this.isInvalid(f))) return;

    // Captcha obligatoire
    const captchaToken = this.captcha?.token ?? '';
    if (!captchaToken || !this.captchaAnswer.trim()) {
      this.error = this.translate.instant('captcha_required');
      return;
    }

    this.loading = true;
    this.error = '';

    this.publicFormService.submitComplaint({
      complainant_name:    this.form.name,
      complainant_phone:   this.form.phone,
      complainant_email:   this.form.email,
      complainant_address: this.form.address,
      complaint_type:      this.form.complaint_type,
      priority:            this.form.priority,
      subject_fr:          this.form.subject,
      description_fr:      this.form.description,
      evidence:            this.evidenceFiles,
      captcha_token:       captchaToken,
      captcha_answer:      this.captchaAnswer,
    }).subscribe({
      next: (res) => {
        this.loading = false;
        this.submitted = true;
        this.reference = res?.complaint_number ?? '';
      },
      error: (err) => {
        this.loading = false;
        this.error = err?.error?.message ?? this.translate.instant('commun_erreur_reseau');
        this.captcha?.refresh();
        this.captchaAnswer = '';
      },
    });
  }

  reset() {
    this.submitted = false;
    this.reference = '';
    this.error = '';
    this.evidenceFiles = [];
    this.touched = {};
    this.captchaAnswer = '';
    this.form = {
      name: '',
      phone: '',
      email: '',
      address: '',
      complaint_type: '',
      priority: 'medium',
      subject: '',
      description: '',
    };
    setTimeout(() => this.captcha?.refresh());
  }
}
